import { CpgDimension } from '@features/campaigns/subdomains/cpg';
import { contentSlideHeader } from '@features/lci-report/utils';
import { buildStyledTable } from '@features/lci-report/utils/build-table';
import { Slide } from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';
import { sanitizeOptimization } from '@utils/valueTransformers';

export const DIMENSION_COUNT = 9;

export type MinimalCpgDimension = Pick<
  CpgDimension,
  | 'dimensionValue'
  | 'impressions'
  | 'uniqueUsers'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'totalProjectedSales'
  | 'averagePricePerTransaction'
  | 'penetration'
  | 'averagePurchaseFrequency'
  | 'salesRate'
  | 'averageTotalBasketAmount'
  | 'salesLiftIndex'
>;

type TableField = {
  columnLabel: string;
  getValue: (dimension: MinimalCpgDimension) => string;
  key: keyof MinimalCpgDimension;
};

const tableFields = [
  {
    columnLabel: 'Dimension',
    getValue: dimension => dimension.dimensionValue,
    key: 'dimensionValue',
  },
  {
    columnLabel: 'Impressions',
    getValue: dimension =>
      formatters.impressions(dimension.impressions, { full: true }),
    key: 'impressions',
  },
  {
    columnLabel: 'Unique Users',
    getValue: dimension =>
      formatters.users(dimension.uniqueUsers, { full: true }),
    key: 'uniqueUsers',
  },
  {
    columnLabel: 'Projected Incr. Sales',
    getValue: dimension =>
      formatters.currency(dimension.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Incremental Sales Lift',
    getValue: dimension => formatters.lift(dimension.incrementalSalesLift),
    key: 'incrementalSalesLift',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: dimension =>
      formatters.currency(dimension.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: dimension =>
      formatters.currency(dimension.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: dimension =>
      formatters.percentage(dimension.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: dimension =>
      cpgFormatters.formatAveragePurchaseFrequency(
        dimension.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Sales Rate',
    getValue: dimension => formatters.currency(dimension.salesRate),
    key: 'salesRate',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: dimension =>
      formatters.currency(dimension.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
  {
    columnLabel: 'Optimization Index',
    getValue: dimension =>
      formatters.number(sanitizeOptimization(dimension.salesLiftIndex), {
        decimals: 2,
      }),
    key: 'salesLiftIndex',
  },
] as const satisfies readonly TableField[];

export type DimensionSlideProps = {
  slideMaster: string;
  title: string;
  dimensions: MinimalCpgDimension[];
  excludedColumns: TableField['key'][];
};

export const dimensionSlide = (props: DimensionSlideProps): Slide => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  return {
    slideMaster: props.slideMaster,
    texts: [contentSlideHeader(props.title)],
    tables: [
      buildStyledTable({
        columns: filteredTableFields.map(field => field.columnLabel),
        rows: props.dimensions
          .slice(0, DIMENSION_COUNT)
          .map(dimension =>
            filteredTableFields.map(field => field.getValue(dimension))
          ),
        options: {
          h: 5.07,
          w: 11.59,
          x: 0.76,
          y: 1.64,
        },
      }),
    ],
  };
};
